<script>
	import CaseStudy from '$lib/Components/CaseStudy/CaseStudy.svelte';
	import Grid from '$lib/Components/Layout/Grid/Grid.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import BuildYourOwnEstimate from '$lib/PageSections/BuildYourOwnEstimate/BuildYourOwnEstimate.svelte';
	import FeaturedArticles from '$lib/PageSections/FeaturedArticles/FeaturedArticles.svelte';
	import GetFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import drafts from './images/drafts.jpg';
	import light from './images/light.jpg';
	import noise from './images/noise.jpg';
</script>

<Section backgroundColor="white">
	<p>
		Multiple studies have been carried out by third-party sources testing the
		efficiency of Indow window inserts across a range of categories. Indow
		inserts have multiple functions and capabilities depending on the grade type
		– read the results and see the performance data of all grade types here.
	</p>

	<Grid columns="3" rows="4" center class="my-8">
		<Grid subgrid vertical>
			<CaseStudy
				src={drafts}
				alt="Window insert performance."
				title="Drafts & Savings"
				href="/solutions/comfort-performance/"
				description="Two independent studies carried out by the U.S. Department of Energy and Portland State University's Green Building Research Laboratory found reductions of 20% to energy consumption when using Indow window inserts, significant savings that allow for lower heating and cooling bills."
				learnMore
				subgrid
			/>
		</Grid>
		<Grid subgrid vertical>
			<CaseStudy
				src={noise}
				alt="Window insert performance."
				href="/solutions/noise-performance"
				title="Noise"
				description="Indow window inserts reduce outside noise penetration by up to 70%. This
				was tested in a study conducted by the British Columbia Institute for
				Technology, as well as by Portland State University's Green Building
				Research Laboratory. Be it over single or double-pane windows, Indow
				inserts are proven to dramatically reduce noise."
				learnMore
				subgrid
			/>
		</Grid>
		<Grid subgrid vertical>
			<CaseStudy
				src={light}
				alt="The sun shines bright overhead on a beautiful day."
				title="Lights & UV"
				href="/solutions/light-uv"
				learnMore
				subgrid
			>
				{#snippet descriptionSlot()}
					<p>
						Each of our specialty grade types which provide <a
							href="/solutions/light-uv/">ways to block sunlight from windows</a
						> is made from a unique high-quality acrylic or medium-density hardwood
						fiberboard, lined with our patented silicone compression fit tubing.
						From light transmittance to UV blocking, get the performance data for
						our Light & UV grade types here.
					</p>
				{/snippet}
			</CaseStudy>
		</Grid>
	</Grid>

	<p>
		Indow is dedicated to continuous improvement and innovation. That’s why we
		continue to test all of our products with the latest technology available.
		This continuous testing is important to us because it ensures we are still
		the best product possible for blocking noise, creating comfort, and saving
		you money. You can read updates about our latest testing on our <a
			href="/resources/featured-articles">blog</a
		> or wait for us to update test results here.
	</p>

	<p>
		At Indow, we have a triple bottom line which includes creating more products
		that are more affordable for more people. That takes rounds and rounds of
		testing to ensure that our new product will perform as well as our first.
		You can watch the kinds of tests we perform <a
			href="https://www.youtube.com/watch?v=wFhy7hrIang"
			target="_blank"
			rel="noreferrer">here.</a
		>
	</p>
</Section>

<Section backgroundColor="white">
	<h2>Consumer Reports &amp; Industry Testing</h2>
	<p>
		A 2019 report on secondary window attachments concluded that window inserts
		benefit all types of spaces, specifically by increasing their efficiency and
		reducing cost. The Commercial Window Attachments report was compiled by
		Northwest Energy Efficiency Alliance (NEEA) and Evergreen Economics to
		understand the current market of secondary windows, their costs, how they
		fit with code, and their performance.
	</p>

	<p>
		To perform their study, NEEA and Evergreen Economics interviewed architects,
		building owners, general contractors, energy services companies and other
		industry experts. They used three case studies to prove the benefits,
		including 2 which featured Indow window inserts. Indow was not contacted
		during this study.
	</p>

	<p>
		Indow is AERC certified. AERC, the Attachments Energy Rating Council, is an
		unaffiliated organization that <a
			href="https://aercenergyrating.org/product-search/residential-product-search"
			rel="external"
			target="_blank"
		>
			rates window attachment products on energy efficiency
		</a> by measuring various factors such as Air Leakage (AL) and U-Factor.
	</p>

	<p>
		On air leakage, Indow scored in the top two out of 33 storm windows tested,
		11 of which were interior storm windows. U-Factor is an indicator of heat
		transfer through a surface. It tells you how well a window product
		insulates. Indow inserts have a U-Factor of .3, making them a top performer
		on insulation among all available storm windows.
	</p>

	<p>
		If you’d like to test Indow inserts in your commercial space, use our
		<a href="/commercial-storm-windows/pilot-program">Pilot Program</a>.
		Architects interested in a sample kit or CSI specs visit our
		<a href="/commercial-storm-windows/historic-tax-credit">
			window preservation inserts</a
		>
		page.
	</p>
</Section>

<Section backgroundColor="white">
	<h2>Window Insert Testing by Field Experts</h2>
	<p>
		Other than submitting our products for testing in official laboratories, we
		have several professionals and experts in their field who have tested Indow
		inserts in their own labs and warehouses:
	</p>

	<p>
		Scott Sidler runs his own historic restoration company, has worked with
		windows for decades, and calls The Craftsman Blog his “laboratory,
		classroom, playground, and favorite place to be”. His video of <a
			href="https://www.youtube.com/watch?v=SdB7Oj1Ayv4"
			target="_blank"
			rel="noreferrer">Indow noise blocking performance</a
		> is a great resource of an expert opinion
	</p>

	<Youtube src="https://www.youtube.com/embed/SdB7Oj1Ayv4" />

	<p>
		Riffs, Beards & Gear is a musician who does audio gear reviews. He got Indow
		window inserts for his home recording studio. He did an <a
			href="https://www.youtube.com/watch?v=eIZpQpx0b0M"
			target="_blank"
			rel="noreferrer">unboxing, installation, and review video</a
		>.
	</p>

	<p>
		One professor and Indow customer used his thermal imaging camera to capture
		the <a href="/resources/blog/custom-storm-windows-save-old-windows"
			>difference in temperature his Indow inserts made</a
		>.
	</p>
</Section>

<BuildYourOwnEstimate />
<GetFreeEstimate backgroundColor="blue" />
<FeaturedArticles />
